import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { Timezone } from 'graph/generated/partner/graphql-types'
import Cookies from 'js-cookie'
import { AppCookie } from 'utils/cookies'

dayjs.extend(timezone)
dayjs.extend(utc)

/**
 * utc: always UTC timezone
 * local: always user local timezone
 * partnerTimezone: partner's timezone from cookies, otherwise local
 */
export type ITypes = 'utc' | 'local' | 'partnerTimezone' | 'timezone'

export const getTimezoneOffset = (
  type: ITypes,
  timezoneName?: string | null
): string => {
  if (type === 'utc') {
    return ''
  }

  const getOffset = (timezone?: string) => {
    let currentDate = dayjs()

    if (timezone) {
      currentDate = currentDate.tz(timezone)
    }

    const utcOffset = currentDate.utcOffset() / 60

    const formatter = new Intl.NumberFormat('en-US', {
      signDisplay: 'always'
    })

    const formattedOffset = formatter.format(utcOffset)

    if (formattedOffset === '+0') {
      return ''
    }

    return `UTC${formatter.format(utcOffset)}`
  }

  if (timezone && timezoneName) {
    return getOffset(timezoneName)
  }

  const partnerTimezone = Cookies.get(AppCookie.PartnerTimezone)

  if (partnerTimezone && type === 'partnerTimezone') {
    return getOffset(partnerTimezone)
  }

  return getOffset()
}

/**
 * utc - date (2025-05-14 00:00:00+00)
 * example: dueDate, dateOfBirth
 *
 * local - datetime (2025-05-14 15:30:23.670862+00)
 * example: createdAt, sentAt
 */
export const formatDate = (
  date: string | Date,
  format: string,
  type: ITypes,
  showTimezone = false
): string => {
  if (!date) return ''

  let parsedDate: Date
  if (typeof date === 'string') {
    parsedDate = new Date(date)
  } else if (date instanceof Date) {
    parsedDate = date
  }

  if (isNaN(parsedDate.getTime())) {
    return ''
  }

  let convertedDate = dayjs(parsedDate)
  if (type === 'utc') {
    convertedDate = convertedDate.utc()
  }

  const partnerTimezone = Cookies.get(AppCookie.PartnerTimezone)
  if (partnerTimezone && type === 'partnerTimezone') {
    convertedDate = convertedDate.tz(partnerTimezone)
  }

  const formattedDate = convertedDate.format(format)

  if (showTimezone) {
    const timezoneOffset = getTimezoneOffset(type)
    return `${formattedDate} (${timezoneOffset})`
  }

  return formattedDate
}

export const formatTimezoneOption = (timezone: string) => {
  const offset = getTimezoneOffset('timezone', timezone)
  const formattedTimezone = timezone
    .replace(/.*?\//, '')
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
  return {
    label: `${formattedTimezone} (${offset})`,
    value: TimezoneToGQLEnumValue[timezone]
  }
}

export const getRelativeTimeText = (date: string): string => {
  if (!date) return ''

  // Anchor "today" in UTC to match `targetDate` (and the UTC-formatted dates
  // these labels sit under). Using local time here caused an off-by-one for
  // users behind UTC — a due date 2 days out rendered as "tomorrow" (AR-7570).
  const today = dayjs.utc().startOf('day')
  const targetDate = dayjs.utc(date).startOf('day')

  if (!targetDate.isValid()) return ''

  const daysDiff = today.diff(targetDate, 'day')
  if (daysDiff === 0) return 'Today'
  return new Intl.RelativeTimeFormat('en', { numeric: 'auto' }).format(
    -daysDiff,
    'day'
  )
}

export const canadaTimezones = [
  'America/St_Johns',
  'America/Halifax',
  'America/Glace_Bay',
  'America/Moncton',
  'America/Goose_Bay',
  'America/Blanc-Sablon',
  'America/Toronto',
  'America/Nipigon',
  'America/Thunder_Bay',
  'America/Iqaluit',
  'America/Pangnirtung',
  'America/Resolute',
  'America/Atikokan',
  'America/Rankin_Inlet',
  'America/Winnipeg',
  'America/Rainy_River',
  'America/Regina',
  'America/Swift_Current',
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Yellowknife',
  'America/Inuvik',
  'America/Creston',
  'America/Dawson_Creek',
  'America/Vancouver',
  'America/Whitehorse',
  'America/Dawson'
]

export const usaTimezones = [
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'America/Metlakatla',
  'Pacific/Honolulu'
]

export const TimezoneToGQLEnumValue = {
  UTC: 'UTC',
  'America/St_Johns': 'AMERICA_ST_JOHNS',
  'America/Halifax': 'AMERICA_HALIFAX',
  'America/Glace_Bay': 'AMERICA_GLACE_BAY',
  'America/Moncton': 'AMERICA_MONCTON',
  'America/Goose_Bay': 'AMERICA_GOOSE_BAY',
  'America/Blanc-Sablon': 'AMERICA_BLANC_SABLON',
  'America/Toronto': 'AMERICA_TORONTO',
  'America/Nipigon': 'AMERICA_NIPIGON',
  'America/Thunder_Bay': 'AMERICA_THUNDER_BAY',
  'America/Iqaluit': 'AMERICA_IQALUIT',
  'America/Pangnirtung': 'AMERICA_PANGNIRTUNG',
  'America/Resolute': 'AMERICA_RESOLUTE',
  'America/Atikokan': 'AMERICA_ATIKOKAN',
  'America/Rankin_Inlet': 'AMERICA_RANKIN_INLET',
  'America/Winnipeg': 'AMERICA_WINNIPEG',
  'America/Rainy_River': 'AMERICA_RAINY_RIVER',
  'America/Regina': 'AMERICA_REGINA',
  'America/Swift_Current': 'AMERICA_SWIFT_CURRENT',
  'America/Edmonton': 'AMERICA_EDMONTON',
  'America/Cambridge_Bay': 'AMERICA_CAMBRIDGE_BAY',
  'America/Yellowknife': 'AMERICA_YELLOWKNIFE',
  'America/Inuvik': 'AMERICA_INUVIK',
  'America/Creston': 'AMERICA_CRESTON',
  'America/Dawson_Creek': 'AMERICA_DAWSON_CREEK',
  'America/Vancouver': 'AMERICA_VANCOUVER',
  'America/Whitehorse': 'AMERICA_WHITEHORSE',
  'America/Dawson': 'AMERICA_DAWSON',
  'America/New_York': 'AMERICA_NEW_YORK',
  'America/Detroit': 'AMERICA_DETROIT',
  'America/Kentucky/Louisville': 'AMERICA_KENTUCKY_LOUISVILLE',
  'America/Kentucky/Monticello': 'AMERICA_KENTUCKY_MONTICELLO',
  'America/Indiana/Indianapolis': 'AMERICA_INDIANA_INDIANAPOLIS',
  'America/Indiana/Vincennes': 'AMERICA_INDIANA_VINCENNES',
  'America/Indiana/Winamac': 'AMERICA_INDIANA_WINAMAC',
  'America/Indiana/Marengo': 'AMERICA_INDIANA_MARENGO',
  'America/Indiana/Petersburg': 'AMERICA_INDIANA_PETERSBURG',
  'America/Indiana/Vevay': 'AMERICA_INDIANA_VEVAY',
  'America/Chicago': 'AMERICA_CHICAGO',
  'America/Indiana/Tell_City': 'AMERICA_INDIANA_TELL_CITY',
  'America/Indiana/Knox': 'AMERICA_INDIANA_KNOX',
  'America/Menominee': 'AMERICA_MENOMINEE',
  'America/North_Dakota/Center': 'AMERICA_NORTH_DAKOTA_CENTER',
  'America/North_Dakota/New_Salem': 'AMERICA_NORTH_DAKOTA_NEW_SALEM',
  'America/North_Dakota/Beulah': 'AMERICA_NORTH_DAKOTA_BEULAH',
  'America/Denver': 'AMERICA_DENVER',
  'America/Boise': 'AMERICA_BOISE',
  'America/Phoenix': 'AMERICA_PHOENIX',
  'America/Los_Angeles': 'AMERICA_LOS_ANGELES',
  'America/Anchorage': 'AMERICA_ANCHORAGE',
  'America/Juneau': 'AMERICA_JUNEAU',
  'America/Sitka': 'AMERICA_SITKA',
  'America/Yakutat': 'AMERICA_YAKUTAT',
  'America/Nome': 'AMERICA_NOME',
  'America/Adak': 'AMERICA_ADAK',
  'America/Metlakatla': 'AMERICA_METLAKATLA',
  'Pacific/Honolulu': 'PACIFIC_HONOLULU'
}

export const GQLEnumValueToTimezone = {
  UTC: 'UTC',
  AMERICA_ST_JOHNS: 'America/St_Johns',
  AMERICA_HALIFAX: 'America/Halifax',
  AMERICA_GLACE_BAY: 'America/Glace_Bay',
  AMERICA_MONCTON: 'America/Moncton',
  AMERICA_GOOSE_BAY: 'America/Goose_Bay',
  AMERICA_BLANC_SABLON: 'America/Blanc-Sablon',
  AMERICA_TORONTO: 'America/Toronto',
  AMERICA_NIPIGON: 'America/Nipigon',
  AMERICA_THUNDER_BAY: 'America/Thunder_Bay',
  AMERICA_IQALUIT: 'America/Iqaluit',
  AMERICA_PANGNIRTUNG: 'America/Pangnirtung',
  AMERICA_RESOLUTE: 'America/Resolute',
  AMERICA_ATIKOKAN: 'America/Atikokan',
  AMERICA_RANKIN_INLET: 'America/Rankin_Inlet',
  AMERICA_WINNIPEG: 'America/Winnipeg',
  AMERICA_RAINY_RIVER: 'America/Rainy_River',
  AMERICA_REGINA: 'America/Regina',
  AMERICA_SWIFT_CURRENT: 'America/Swift_Current',
  AMERICA_EDMONTON: 'America/Edmonton',
  AMERICA_CAMBRIDGE_BAY: 'America/Cambridge_Bay',
  AMERICA_YELLOWKNIFE: 'America/Yellowknife',
  AMERICA_INUVIK: 'America/Inuvik',
  AMERICA_CRESTON: 'America/Creston',
  AMERICA_DAWSON_CREEK: 'America/Dawson_Creek',
  AMERICA_VANCOUVER: 'America/Vancouver',
  AMERICA_WHITEHORSE: 'America/Whitehorse',
  AMERICA_DAWSON: 'America/Dawson',
  AMERICA_NEW_YORK: 'America/New_York',
  AMERICA_DETROIT: 'America/Detroit',
  AMERICA_KENTUCKY_LOUISVILLE: 'America/Kentucky/Louisville',
  AMERICA_KENTUCKY_MONTICELLO: 'America/Kentucky/Monticello',
  AMERICA_INDIANA_INDIANAPOLIS: 'America/Indiana/Indianapolis',
  AMERICA_INDIANA_VINCENNES: 'America/Indiana/Vincennes',
  AMERICA_INDIANA_WINAMAC: 'America/Indiana/Winamac',
  AMERICA_INDIANA_MARENGO: 'America/Indiana/Marengo',
  AMERICA_INDIANA_PETERSBURG: 'America/Indiana/Petersburg',
  AMERICA_INDIANA_VEVAY: 'America/Indiana/Vevay',
  AMERICA_CHICAGO: 'America/Chicago',
  AMERICA_INDIANA_TELL_CITY: 'America/Indiana/Tell_City',
  AMERICA_INDIANA_KNOX: 'America/Indiana/Knox',
  AMERICA_MENOMINEE: 'America/Menominee',
  AMERICA_NORTH_DAKOTA_CENTER: 'America/North_Dakota/Center',
  AMERICA_NORTH_DAKOTA_NEW_SALEM: 'America/North_Dakota/New_Salem',
  AMERICA_NORTH_DAKOTA_BEULAH: 'America/North_Dakota/Beulah',
  AMERICA_DENVER: 'America/Denver',
  AMERICA_BOISE: 'America/Boise',
  AMERICA_PHOENIX: 'America/Phoenix',
  AMERICA_LOS_ANGELES: 'America/Los_Angeles',
  AMERICA_ANCHORAGE: 'America/Anchorage',
  AMERICA_JUNEAU: 'America/Juneau',
  AMERICA_SITKA: 'America/Sitka',
  AMERICA_YAKUTAT: 'America/Yakutat',
  AMERICA_NOME: 'America/Nome',
  AMERICA_ADAK: 'America/Adak',
  AMERICA_METLAKATLA: 'America/Metlakatla',
  PACIFIC_HONOLULU: 'Pacific/Honolulu'
}

export const getTimezoneFromGQLEnum = (timezoneGQLEnumValue?: string) => {
  const timezone = Object.keys(TimezoneToGQLEnumValue).find(
    key => TimezoneToGQLEnumValue[key] === timezoneGQLEnumValue
  )

  if (timezone) return timezone

  return getTimezoneFromGQLEnum(Timezone.AmericaNewYork)
}

export const formatTimezoneLabel = (timezone: string) =>
  // 'America/New_York' -> 'New York'
  timezone.replace(/.*?\//, ``).replace(/_/g, ' ')

/**
 * Formats a date as YYYY-MM-DD in UTC without timezone shifting.
 * Uses Intl.DateTimeFormat to ensure consistent formatting across all locales.
 *
 * This function is particularly useful for date inputs where you want to display
 * and manipulate dates in UTC without any timezone conversions that could cause
 * dates to shift forward or backward.
 *
 * @param date - Date string or Date object to format
 * @returns Formatted date string in YYYY-MM-DD format (e.g., "2025-01-01")
 * @example
 * formatDateUTC(new Date('2025-01-01T00:00:00Z')) // "2025-01-01"
 * formatDateUTC('2025-01-01') // "2025-01-01"
 */
export const formatDateUTC = (date: Date | string): string => {
  const d = new Date(date)
  if (isNaN(d.getTime())) return ''

  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC'
  }).format(d)
}

/**
 * Converts a Date object or date string to an ISO string in UTC format,
 * preserving the local date the user selected (not the UTC date).
 *
 * This is useful for date-only fields (like bill due dates, birth dates) where
 * the user's selected date should be preserved regardless of their timezone.
 *
 * @param date - Date object or date string to convert
 * @returns ISO string in UTC format (e.g., "2025-01-15T00:00:00.000Z")
 * @example
 * // From Date object - User in Toronto (UTC-5) selects Jan 15 at 10pm local
 * toISOStringDateOnlyUTC(date) // "2025-01-15T00:00:00.000Z"
 *
 * // From date-only string
 * toISOStringDateOnlyUTC('2025-01-15') // "2025-01-15T00:00:00.000Z"
 */
export const toISOStringDateOnlyUTC = (date: Date | string): string => {
  if (typeof date === 'string') {
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(date)
    if (isDateOnly) {
      return `${date}T00:00:00.000Z`
    }
    // Parse string to Date and extract local date components
    date = new Date(date)
  }

  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}T00:00:00.000Z`
}

/**
 * Parses a UTC ISO date string and creates a Date object that preserves the
 * original date in the user's local timezone.
 *
 * This is the inverse of toISOStringFromDateUTC. Use this when loading date-only
 * fields (like bill due dates) to ensure the DatePicker displays the same date
 * that was saved, regardless of the user's timezone.
 *
 * @param isoString - ISO date string (e.g., "2025-01-15T00:00:00.000Z" or "2025-01-15")
 * @returns Date object representing the same date in local timezone
 * @example
 * // Backend returns "2025-01-15T00:00:00.000Z"
 * // User in Toronto (UTC-5) should see "Jan 15" not "Jan 14"
 * parseDateFromUTC("2025-01-15T00:00:00.000Z") // Date representing Jan 15 local time
 */
export const parseDateFromUTC = (isoString: string): Date => {
  // Extract just the date part (YYYY-MM-DD)
  const datePart = isoString.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  // Create date in local timezone (months are 0-indexed)
  return new Date(year, month - 1, day)
}

/**
 * Clamps a date string to the start of the local day (00:00:00.000) and
 * returns it as an ISO string, or `undefined` when no date is provided.
 * Canonical day-boundary helper shared by the Payments and Payouts filters.
 */
export const startOfDay = (date?: string | null): string | undefined => {
  if (!date) return undefined

  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  return d.toISOString()
}

/**
 * Clamps a date string to the end of the local day (23:59:59.999) and
 * returns it as an ISO string, or `undefined` when no date is provided.
 * Canonical day-boundary helper shared by the Payments and Payouts filters.
 */
export const endOfDay = (date?: string | null): string | undefined => {
  if (!date) return undefined

  const d = new Date(date)
  d.setHours(23, 59, 59, 999)
  return d.toISOString()
}
